import { DonorStatus, useGetRegistrationDonorsQuery } from 'gql/types';
import { useMemo } from 'react';

import { IDonorRecord } from '../types/donor';

interface IUseRegistrationDonorsProps {
  enabled: boolean;
  registrationRange?: { start: string; end: string };
  regionIds?: string[];
  locationIds?: string[];
  officeIds?: string[];
  sglThresholdMl?: number;
}

export function useRegistrationDonors({
  enabled,
  registrationRange,
  regionIds,
  locationIds,
  officeIds,
  sglThresholdMl,
}: IUseRegistrationDonorsProps) {
  const periodRange = useMemo(() => {
    if (!registrationRange) return undefined;
    return {
      start: new Date(registrationRange.start),
      end: new Date(registrationRange.end),
    };
  }, [registrationRange]);

  const { data, loading } = useGetRegistrationDonorsQuery({
    skip: !enabled || !periodRange,
    variables: {
      input: {
        periodRange: periodRange
          ? { start: periodRange.start, end: periodRange.end }
          : { start: new Date(), end: new Date() },
        regionIds: regionIds?.length ? regionIds : undefined,
        locationIds: locationIds?.length ? locationIds : undefined,
        officeIds: officeIds?.length ? officeIds : undefined,
        sglThresholdMl,
      },
    },
  });

  const donors: IDonorRecord[] = useMemo(
    () =>
      (data?.getRegistrationDonors?.donors ?? []).map((d) => ({
        donorId: d.donorId,
        donorShortId: d.donorShortId,
        donorName: d.donorName,
        ownerFullName: d.ownerFullName ?? null,
        ownerShortId: d.ownerShortId ?? null,
        bloodTypeDisplay: d.bloodTypeDisplay,
        status: d.status as DonorStatus,
        lastDonationDate: d.lastDonationDate ?? null,
        sglDbl: d.sglDbl,
        totalDonations: d.totalDonations,
        periodUnits: d.periodUnits,
        location: d.location,
        registeredAt: d.registeredAt ?? null,
        ownerPhone: d.ownerPhone ?? null,
        ownerEmail: d.ownerEmail ?? null,
        ownerCity: d.ownerCity ?? null,
        ownerState: d.ownerState ?? null,
        ownerContactPreferences: d.ownerContactPreferences ?? null,
        ownerAssignedLocations: d.ownerAssignedLocations ?? null,
        breed: d.breed ?? null,
        dateOfBirth: d.dateOfBirth ?? null,
        region: d.region ?? null,
        retiredDate: null,
        retiredReason: null,
        activeDate: d.activeDate ?? null,
        approvedDate: null,
        archivedDate: null,
      })),
    [data]
  );

  return {
    donors,
    total: data?.getRegistrationDonors?.total ?? 0,
    loading,
  };
}
